import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import {
  remoteApprove,
  remoteCloseRoom,
  remoteCreateRoom,
  remoteDeny,
  remoteGetRoom,
  remoteKick,
} from '../utils/remoteApi';
import { buildRemoteJoinUrl } from '../utils/remoteJoinPath';

const SESSION_KEY = 'deckpack-remote-operator';
function loadSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o?.roomId && o?.operatorSecret) return { roomId: o.roomId, operatorSecret: o.operatorSecret };
  } catch {
    /* ignore */
  }
  return null;
}

function saveSession(s) {
  if (s) sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
  else sessionStorage.removeItem(SESSION_KEY);
}

const NOTIFY_PREF_KEY = 'deckpack-remote-notify-pending';

export default function RemoteOperatorModal({
  open,
  onClose,
  initialSession,
  idleErrorHint = '',
  onSession,
  onBeforeEndSession,
  onDisconnectPeer,
  onApprovePeer,
  onJoinNotifySettingsChange,
}) {
  const [session, setSession] = useState(initialSession);
  const [pending, setPending] = useState([]);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [notifyPref, setNotifyPref] = useState(() => {
    try {
      return localStorage.getItem(NOTIFY_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [notifyPermission, setNotifyPermission] = useState(
    () => (typeof Notification !== 'undefined' ? Notification.permission : 'denied')
  );
  const skipPollUntilRef = useRef(0);
  const copyFeedbackTimerRef = useRef(null);

  const fullJoinUrl = useMemo(() => {
    if (!session?.roomId || typeof window === 'undefined') return '';
    return buildRemoteJoinUrl(window.location.origin, session.roomId);
  }, [session?.roomId]);

  useEffect(() => {
    if (open) {
      const s = initialSession || loadSession();
      setSession(s);
      if (s) onSession?.(s);
    } else {
      setLinkCopied(false);
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
        copyFeedbackTimerRef.current = null;
      }
    }
  }, [open, initialSession, onSession]);

  useEffect(
    () => () => {
      if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!open || typeof Notification === 'undefined') return;
    setNotifyPermission(Notification.permission);
  }, [open]);

  const setNotifyOptIn = useCallback(
    (enabled) => {
      try {
        if (enabled) localStorage.setItem(NOTIFY_PREF_KEY, '1');
        else localStorage.removeItem(NOTIFY_PREF_KEY);
      } catch {
        /* ignore */
      }
      setNotifyPref(enabled);
      onJoinNotifySettingsChange?.();
    },
    [onJoinNotifySettingsChange]
  );

  const requestNotifyPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const p = await Notification.requestPermission();
      setNotifyPermission(p);
      if (p === 'granted') {
        try {
          localStorage.setItem(NOTIFY_PREF_KEY, '1');
        } catch {
          /* ignore */
        }
        setNotifyPref(true);
      }
      onJoinNotifySettingsChange?.();
    } catch {
      setNotifyPermission(Notification.permission);
    }
  }, [onJoinNotifySettingsChange]);

  const refresh = useCallback(async (force) => {
    if (!force && Date.now() < skipPollUntilRef.current) return;
    const s = session || loadSession();
    if (!s) return;
    setError('');
    try {
      const data = await remoteGetRoom(s.roomId, s.operatorSecret);
      if (Date.now() < skipPollUntilRef.current) return;
      setPending(data.pending || []);
      setClients((data.clients || []).filter((c) => c.status !== 'pending'));
    } catch (e) {
      setError(e?.message || 'Could not load remote room');
    }
  }, [session]);

  useEffect(() => {
    if (!open || !session) return undefined;
    refresh();
    const t = window.setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [open, session, refresh]);

  const copyJoinLink = useCallback(async () => {
    if (!fullJoinUrl) return;
    try {
      await navigator.clipboard.writeText(fullJoinUrl);
      setLinkCopied(true);
      if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = setTimeout(() => {
        setLinkCopied(false);
        copyFeedbackTimerRef.current = null;
      }, 2500);
    } catch {
      setError('Could not copy — select the link manually or try again.');
    }
  }, [fullJoinUrl]);

  const startRoom = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await remoteCreateRoom();
      const s = { roomId: data.roomId, operatorSecret: data.operatorSecret };
      setSession(s);
      saveSession(s);
      onSession?.(s);
      setPending([]);
      setClients([]);
    } catch (e) {
      setError(e?.message || 'Could not create remote room. Is the REMOTE_ROOM Durable Object bound?');
    } finally {
      setBusy(false);
    }
  };

  const stopRoom = async () => {
    const s = session || loadSession();
    setBusy(true);
    setError('');
    try {
      onBeforeEndSession?.();
      await new Promise((r) => setTimeout(r, 120));
      if (s) {
        await remoteCloseRoom(s.roomId, s.operatorSecret);
      }
    } catch (e) {
      setError(
        e?.name === 'AbortError'
          ? 'End session timed out — cleared on this computer.'
          : e?.message || 'Could not close room on server.'
      );
    } finally {
      saveSession(null);
      onSession?.(null);
      setSession(null);
      setPending([]);
      setClients([]);
      setBusy(false);
      onClose?.();
    }
  };

  if (!open) return null;

  const topError = session ? error : error || idleErrorHint;

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="decks-modal remote-operator-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Remote</h2>
          <button type="button" className="close-modal-button" onClick={onClose} aria-label="Close">
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>
        <div className="remote-operator-body">
          {topError ? <p className="remote-operator-error">{topError}</p> : null}

          {!session ? (
            <p className="remote-operator-lead remote-operator-lead--start">
              Start a session for a join link and QR code.
            </p>
          ) : null}

          {!session ? (
            <div className="remote-operator-actions">
              <button type="button" className="remote-primary-btn" disabled={busy} onClick={startRoom}>
                {busy ? 'Starting…' : 'Start remote session'}
              </button>
            </div>
          ) : (
            <>
              {pending.length ? (
                <div className="remote-list-section remote-list-section--pending-first">
                  <h3>Waiting</h3>
                  <ul className="remote-pending-list">
                    {pending.map((p) => (
                      <li key={p.id}>
                        <span className="remote-pending-name">
                          {p.name}
                          {p.device ? <span className="remote-device-label">{p.device}</span> : null}
                        </span>
                        <button
                          type="button"
                          className="remote-small-btn remote-approve"
                          onClick={async () => {
                            skipPollUntilRef.current = Date.now() + 5000;
                            setPending((prev) => prev.filter((x) => x.id !== p.id));
                            try {
                              const res = await remoteApprove(session.roomId, session.operatorSecret, p.id);
                              if (res.approvedToken) onApprovePeer?.(p.id, res.approvedToken);
                              if (res.pending) setPending(res.pending);
                              if (res.clients) setClients(res.clients.filter((c) => c.status !== 'pending'));
                            } catch (e) {
                              setError(e?.message || 'Approve failed');
                              skipPollUntilRef.current = 0;
                              refresh(true);
                            }
                          }}
                        >
                          Allow
                        </button>
                        <button
                          type="button"
                          className="remote-small-btn remote-deny"
                          onClick={async () => {
                            skipPollUntilRef.current = Date.now() + 5000;
                            setPending((prev) => prev.filter((x) => x.id !== p.id));
                            try {
                              const res = await remoteDeny(session.roomId, session.operatorSecret, p.id);
                              if (res.pending) setPending(res.pending);
                              if (res.clients) setClients(res.clients.filter((c) => c.status !== 'pending'));
                            } catch (e) {
                              setError(e?.message || 'Deny failed');
                              skipPollUntilRef.current = 0;
                              refresh(true);
                            }
                          }}
                        >
                          Deny
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="remote-connect-panel remote-connect-panel--compact">
                <p className="remote-connect-lead remote-connect-lead--compact">
                  Scan or copy the link. You approve each device before it controls slides.
                </p>

                <div className="remote-connect-qr-wrap remote-connect-qr-wrap--compact" aria-hidden={!fullJoinUrl}>
                  {fullJoinUrl ? (
                    <QRCode value={fullJoinUrl} size={132} level="M" className="remote-connect-qr-svg" />
                  ) : null}
                </div>

                <div className="remote-connect-host-row">
                  <code className="remote-connect-host" title={fullJoinUrl}>
                    {fullJoinUrl || '…'}
                  </code>
                  <button
                    type="button"
                    className={`remote-primary-btn remote-connect-copy${linkCopied ? ' remote-connect-copy--done' : ''}`}
                    onClick={copyJoinLink}
                  >
                    {linkCopied ? (
                      <>
                        <i className="fa-solid fa-check" aria-hidden="true" /> Copied
                      </>
                    ) : (
                      'Copy'
                    )}
                  </button>
                </div>

                <p className="remote-connect-code-hint">
                  Code <span className="remote-connect-code">{session.roomId}</span>
                </p>

                {typeof Notification !== 'undefined' ? (
                  <div className="remote-notify-compact">
                    {notifyPermission === 'denied' ? (
                      <span className="remote-notify-compact-text">Unblock notifications in browser settings for join alerts.</span>
                    ) : notifyPermission !== 'granted' ? (
                      <button type="button" className="remote-notify-compact-btn" onClick={requestNotifyPermission}>
                        Allow join alerts
                      </button>
                    ) : (
                      <label className="remote-notify-compact-toggle">
                        <input
                          type="checkbox"
                          checked={notifyPref}
                          onChange={(e) => setNotifyOptIn(e.target.checked)}
                        />
                        <span>Notify when someone joins (Remote closed)</span>
                      </label>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="remote-list-section">
                <h3>Connected</h3>
                {clients.length === 0 ? (
                  <p className="remote-empty">None yet.</p>
                ) : (
                  <ul className="remote-client-list">
                    {clients.map((c) => (
                      <li key={c.id}>
                        <div className="remote-client-info">
                          <span className="remote-client-name">{c.name}</span>
                          {c.device ? <span className="remote-device-label">{c.device}</span> : null}
                          <span className={`remote-client-status remote-client-status--${c.status}`}>
                            {c.blocked ? 'blocked' : c.status}
                          </span>
                        </div>
                        <div className="remote-client-actions">
                          {c.status === 'approved' && !c.blocked ? (
                            <button
                              type="button"
                              className="remote-small-btn remote-kick"
                              onClick={async () => {
                                onDisconnectPeer?.(c.id);
                                await remoteKick(session.roomId, session.operatorSecret, c.id);
                                refresh();
                              }}
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="remote-operator-footer-actions">
                <button type="button" className="remote-danger-btn" disabled={busy} onClick={stopRoom}>
                  End remote session
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export { loadSession as loadRemoteOperatorSession, saveSession as saveRemoteOperatorSession };
